import React from 'react';
import { Dialog, IconButton, Button, Stack } from '@mui/material';
import { MdClose } from 'react-icons/md';
import { Icon } from '@iconify/react';

/**
 * WhatsAppConfirmationDialog - Reusable confirmation dialog before sending WhatsApp notification
 *
 * Props:
 *  open         - boolean: whether dialog is visible
 *  onClose      - fn: called when user clicks No or closes
 *  onConfirm    - fn: called when user clicks Yes (should trigger the API call)
 *  userName     - string: name of the user
 *  phoneNumber  - string: phone number to display
 *  loading      - boolean: disable Yes button while sending
 */
const WhatsAppConfirmationDialog = ({ open, onClose, onConfirm, userName, phoneNumber, loading = false }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <div className="card h-100 radius-12 text-center position-relative mb-0">
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'background.default',
            color: 'text.primary',
            '&:hover': { bgcolor: 'background.paper' },
          }}
          size="small"
        >
          <MdClose size={20} />
        </IconButton>

        <div className="card-body p-24">
          {/* WhatsApp Icon */}
          <div className="w-64-px h-64-px d-inline-flex align-items-center justify-content-center bg-info-600 text-white mb-16 radius-12">
            <Icon icon="ic:baseline-whatsapp" className="h5 mb-0" style={{ fontSize: '32px' }} />
          </div>

          {/* User Name */}
          {userName && <h6 className="mb-4">{userName}</h6>}

          {/* Phone Number */}
          {phoneNumber && (
            <p className="card-text mb-8 text-secondary-light fw-semibold" style={{ fontSize: '22px' }}>
              {phoneNumber}
            </p>
          )}

          {/* Confirmation Message */}
          <p className="text-secondary-light text-md mb-20">
            Are you sure you want to send a WhatsApp notification?
          </p>

          {/* Action Buttons */}
          <Stack direction="row" justifyContent="center" spacing={2}>
            <Button
              variant="outlined"
              color="error"
              onClick={onClose}
              disabled={loading}
              sx={{ borderRadius: 2, px: 3 }}
            >
              No
            </Button>
            <Button
              variant="contained"
              color="info"
              onClick={onConfirm}
              startIcon={<Icon icon="ic:baseline-whatsapp" />}
              disabled={loading}
              sx={{ borderRadius: 2, px: 3 }}
            >
              {loading ? 'Sending...' : 'Yes'}
            </Button>
          </Stack>
        </div>
      </div>
    </Dialog>
  );
};

export default WhatsAppConfirmationDialog;
